// Stem must not match HeaderSearchRecents.tsx. Vite tries .ts before .tsx, so a
// case-insensitive disk would load this file for `import .../HeaderSearchRecents`.
/** Header dropdown shows this many of each list. */
export const HEADER_DROPDOWN_RECENT_LIMIT = 3;
/** Search results page shows this many. Preferences keep the same count. */
export const SEARCH_PAGE_RECENT_LIMIT = 6;

export const EMPTY_HEADER_SEARCH: user_prefs.HeaderSearchPreference = {
  recentSearches: [],
  recentlyViewedPeople: [],
};

export type HeaderSearchLists = {
  recentSearches: user_prefs.RecentSearch[];
  recentlyViewedPeople: user_prefs.RecentlyViewedPerson[];
  recentlyViewed?: user_prefs.RecentlyViewedItem[];
};

const sameSearch = (
  left: user_prefs.RecentSearch,
  right: user_prefs.RecentSearch
) =>
  left.kind === right.kind &&
  left.query.trim().toLowerCase() === right.query.trim().toLowerCase();

const hasViewedList = (
  current: user_prefs.HeaderSearchPreference | null | undefined
): current is user_prefs.HeaderSearchPreference & {
  recentlyViewed: user_prefs.RecentlyViewedItem[];
} => Array.isArray(current?.recentlyViewed);

export const isBlankHeaderSearchTerm = (term: string | null | undefined) =>
  !String(term ?? "").trim();

const initialsForViewedName = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) {
    return "";
  }
  if (parts.length === 1) {
    return first.slice(0, 2).toUpperCase();
  }
  const last = parts[parts.length - 1] ?? first;
  return `${first[0] ?? ""}${last[0] ?? ""}`.toUpperCase();
};

export const viewedFromLabel = (
  kind: user_prefs.RecentlyViewedKind,
  id: string | null | undefined,
  name: string | null | undefined,
  subtitle = "",
  extras?: { initials?: string; open?: boolean }
): user_prefs.RecentlyViewedItem | null => {
  const trimmed = String(name ?? "").trim();
  if (!id || !trimmed) {
    return null;
  }
  const initials = extras?.initials?.trim() || initialsForViewedName(trimmed);
  return {
    id,
    kind,
    name: trimmed,
    initials,
    subtitle,
    ...(extras?.open === undefined ? {} : { open: extras.open }),
  };
};

export const withHeaderSearchLists = (
  current: user_prefs.HeaderSearchPreference | null | undefined
): HeaderSearchLists => {
  const lists: HeaderSearchLists = {
    recentSearches: (current?.recentSearches ?? []).map((search) =>
      search.kind === "role" ? { ...search, kind: "tag" } : search
    ),
    recentlyViewedPeople: current?.recentlyViewedPeople ?? [],
  };
  if (hasViewedList(current)) {
    lists.recentlyViewed = current.recentlyViewed;
  }
  return lists;
};

const personFromViewed = (
  item: user_prefs.RecentlyViewedItem
): user_prefs.RecentlyViewedPerson => ({
  id: item.id,
  name: item.name,
  initials: item.initials,
  subtitle: item.subtitle,
});

export const listRecentlyViewed = (
  current: user_prefs.HeaderSearchPreference | null | undefined
): user_prefs.RecentlyViewedItem[] => {
  if (hasViewedList(current)) {
    return current.recentlyViewed.slice(0, SEARCH_PAGE_RECENT_LIMIT);
  }
  return (current?.recentlyViewedPeople ?? [])
    .slice(0, SEARCH_PAGE_RECENT_LIMIT)
    .map((person) => ({
      id: person.id,
      kind: "person" as const,
      name: person.name,
      initials: person.initials,
      subtitle: person.subtitle,
    }));
};

export const pushRecentSearch = (
  current: user_prefs.HeaderSearchPreference | null | undefined,
  item: user_prefs.RecentSearch
): HeaderSearchLists => {
  const query = item.query.trim();
  const stored = withHeaderSearchLists(current);
  if (!query) {
    return stored;
  }
  const next = { ...item, query };
  return {
    ...stored,
    recentSearches: [
      next,
      ...stored.recentSearches.filter(
        (existing) => !sameSearch(existing, next)
      ),
    ].slice(0, SEARCH_PAGE_RECENT_LIMIT),
  };
};

export const pushViewedItem = (
  current: user_prefs.HeaderSearchPreference | null | undefined,
  item: user_prefs.RecentlyViewedItem
): HeaderSearchLists => {
  const stored = withHeaderSearchLists(current);
  const name = item.name.trim();
  if (!item.id || !name) {
    return stored;
  }
  const next = { ...item, name };
  const recentlyViewed = [
    next,
    ...listRecentlyViewed(current).filter(
      (existing) => !(existing.kind === next.kind && existing.id === next.id)
    ),
  ].slice(0, SEARCH_PAGE_RECENT_LIMIT);
  return {
    ...stored,
    recentlyViewed,
    recentlyViewedPeople: recentlyViewed
      .filter((existing) => existing.kind === "person")
      .map(personFromViewed),
  };
};

export const pushViewedPerson = (
  current: user_prefs.HeaderSearchPreference | null | undefined,
  person: user_prefs.RecentlyViewedPerson
): HeaderSearchLists =>
  pushViewedItem(current, {
    id: person.id,
    kind: "person",
    name: person.name,
    initials: person.initials,
    subtitle: person.subtitle,
  });

export const clearRecentSearches = (
  current: user_prefs.HeaderSearchPreference | null | undefined
): HeaderSearchLists => ({
  ...withHeaderSearchLists(current),
  recentSearches: [],
});

export const recentSearchKindLabel = (
  kind: user_prefs.RecentSearchKind
): string => {
  switch (kind) {
    case "person":
      return "Person";
    case "team":
      return "Team";
    case "tag":
    case "role":
      return "Tag";
    case "position":
      return "Position";
    case "query":
      return "Search";
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
};
